<script setup lang="ts">
import type { IUiTabsOption, IUiTabsProps } from './UiTabs.types'
import { UI_TABS_VARIANT } from './UiTabs.types'

const props = withDefaults(defineProps<IUiTabsProps>(), {
  disabled: false,
  variant: UI_TABS_VARIANT.PILL,
})

const modelValue = defineModel<string | number | null>()

const tabRefs = ref<HTMLButtonElement[]>([])

const focusIndex = computed(() => {
  const i = props.options.findIndex(o => o.value === modelValue.value)
  return i >= 0 ? i : 0
})

const select = (option: IUiTabsOption) => {
  if (props.disabled) return
  modelValue.value = option.value
}

const onKeydown = (e: KeyboardEvent, index: number) => {
  if (props.disabled) return
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

  e.preventDefault()

  const last = props.options.length - 1
  let next = e.key === 'ArrowRight' ? index + 1 : index - 1

  if (next > last) next = 0
  if (next < 0) next = last

  select(props.options[next]!)
  tabRefs.value[next]?.focus()
}
</script>

<template>
  <div
    :class="['uiTabs', `uiTabs--${variant}`]"
    role="radiogroup"
    :aria-label="label"
    :aria-disabled="disabled"
  >
    <span
      v-if="label"
      class="uiTabs__label"
    >
      {{ label }}
    </span>

    <div class="uiTabs__group">
      <button
        v-for="(option, i) in options"
        :key="option.value"
        ref="tabRefs"
        type="button"
        :class="[
          'uiTabs__tab',
          { 'uiTabs__tab--selected': option.value === modelValue }
        ]"
        :disabled="disabled"
        :tabindex="i === focusIndex ? 0 : -1"
        role="radio"
        :aria-checked="option.value === modelValue"
        @click="select(option)"
        @keydown="onKeydown($event, i)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.uiTabs {
  $root: &;

  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  &__label {
    @include body-2-medium;
    color: $text-400;
  }

  &__group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  &__tab {
    @include body-3-medium;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    padding: 0.8rem 1.2rem;
    color: $text-400;
    background-color: $white;
    border: 0.1rem solid $gray-400;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

    &:hover:not(:disabled) {
      border-color: $text-300;
      box-shadow: inset 0 0 0 0.1rem $text-300;
    }

    &:focus-visible {
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.2rem;
    }

    &:disabled {
      color: $text-200;
      cursor: not-allowed;
    }

    &--selected {
      background-color: $gray-100;
      border-color: $text-300;
    }
  }

  &--pill #{$root}__tab {
    border-radius: 5rem;
  }

  &--rounded #{$root}__tab {
    border-radius: 0.8rem;
  }

  &[aria-disabled="true"] {
    #{$root}__label {
      color: $text-200;
    }
  }
}
</style>
